import type { ClientStatus, LetterRecommendation } from '@frontend/api-types';
import { useEffect, useFeatureValue, useVisibilityController } from '@frontend/react';
import { FlatList, LinkButton, Text, XStack, YStack } from '@frontend/web-react';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { LetterRecommendationCard } from './LetterRecommendationCard';
import { LetterRecommendationDetailModal } from './LetterRecommendationDetailModal';

type LetterRecommendationsContainerProps = {
  clientStatus?: ClientStatus;
  letterRecommendations: LetterRecommendation[];
};

export const LetterRecommendationsContainer = ({
  clientStatus,
  letterRecommendations,
}: LetterRecommendationsContainerProps) => {
  const { value: unreadLetterThreshold } = useFeatureValue('unread_letter_threshold');
  const { value: letterRecommendationActiveTaskThreshold } = useFeatureValue(
    'letter_recommendation_active_task_threshold',
  );
  const navigate = useNavigate();
  const [
    isLetterRecommendationDetailModalOpen,
    { close: closeLetterRecommendationDetailModal, open: openLetterRecommendationDetailModal },
  ] = useVisibilityController();
  const [selectedLetterRecommendationId, setSelectedLetterRecommendationId] = useState<string | null>(null);

  const handleLetterRecommendationPress = (id: string) => {
    setSelectedLetterRecommendationId(id);
    openLetterRecommendationDetailModal();
  };

  const handleCloseDetailModal = () => {
    setSelectedLetterRecommendationId(null);
    closeLetterRecommendationDetailModal();
  };

  useEffect(() => {
    if (
      letterRecommendations?.length &&
      unreadLetterThreshold &&
      letterRecommendationActiveTaskThreshold &&
      clientStatus?.currentActiveTasks
    ) {
      // check if member hasn't read last two letters
      const unreadLetters = letterRecommendations.slice(0, unreadLetterThreshold).filter((letter) => !letter.viewedAt);
      if (
        unreadLetters.length >= unreadLetterThreshold &&
        clientStatus?.currentActiveTasks <= letterRecommendationActiveTaskThreshold &&
        letterRecommendations[0]
      ) {
        handleLetterRecommendationPress(letterRecommendations[0].id);
      }
    }
  }, [clientStatus, unreadLetterThreshold, letterRecommendationActiveTaskThreshold, letterRecommendations]);

  return (
    <YStack className="gap-md flex-1">
      <YStack className="px-md" gap="xs">
        <XStack className="w-full items-center justify-between">
          <Text type="h4">{"Bill's"} weekly picks</Text>
          {letterRecommendations.length > 3 ? (
            <LinkButton
              color="secondary"
              label="See all"
              onClick={() => navigate({ to: '/app/letter-recommendation' })}
              type="p-sm"
            />
          ) : null}
        </XStack>
        <Text color="secondary" type="caption">
          Weekly tasks crafted just for you.
        </Text>
      </YStack>
      <FlatList
        contentContainerClassName="gap-md grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 px-md"
        data={letterRecommendations.slice(0, 3)}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <LetterRecommendationCard
            letterRecommendation={item}
            onPress={() => handleLetterRecommendationPress(item.id)}
          />
        )}
      />
      {selectedLetterRecommendationId ? (
        <LetterRecommendationDetailModal
          isOpen={!!selectedLetterRecommendationId && isLetterRecommendationDetailModalOpen}
          letterRecommendationId={selectedLetterRecommendationId}
          onClose={handleCloseDetailModal}
        />
      ) : null}
    </YStack>
  );
};
